import { Injectable } from '@angular/core';
import {
  defaultSystemConfig,
  defaultWorkflowConfig,
  hydrateSystemConfigCssConfigs,
  PageTemplateConfig,
  SystemConfiguration,
  WorkflowConfig,
} from '@finxone-platform/shared/sys-config-types';
import { mapWorkflowToSystem } from '@finxone-platform/shared/workflow-mapping';
import LZString from 'lz-string';
import { BehaviorSubject, first, firstValueFrom, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SystemConfigService {
  private _systemConfig$: BehaviorSubject<SystemConfiguration> = new BehaviorSubject<SystemConfiguration>(
    defaultSystemConfig,
  );

  private _pageTemplate$: BehaviorSubject<PageTemplateConfig> = new BehaviorSubject<PageTemplateConfig>(
    undefined as any,
  );

  constructor() {
    this.updateConfig(() => {
      return mapWorkflowToSystem(this.getWorkFlowConfigFromLocalStorage());
    });
    localStorage.removeItem('systemConfig');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)['systemConfigService'] = this;
  }

  getConfigSnapshot() {
    return this._systemConfig$.getValue();
  }

  setPageTemplate(config: PageTemplateConfig) {
    this._pageTemplate$.next(config);
  }

  getPageTemplateConfig(): Observable<PageTemplateConfig> {
    return this._pageTemplate$;
  }

  getConfig(): Observable<SystemConfiguration> {
    return this._systemConfig$.asObservable();
  }

  updateConfig(
    fn: (config: SystemConfiguration) => SystemConfiguration,
    isGridPositionUpdate = false,
  ): Promise<SystemConfiguration> {
    return firstValueFrom(
      this.getConfig().pipe(
        first(),
        tap((config) => {
          // mapWorkflowToSystem keeps css_configs pruned; the builder preview
          // renders from this subject, so restore the defaults here
          const updatedConfig = hydrateSystemConfigCssConfigs(fn(config));
          if (!isGridPositionUpdate) this._systemConfig$.next(updatedConfig);
        }),
      ),
    );
  }

  private getWorkFlowConfigFromLocalStorage(): WorkflowConfig {
    if (localStorage.getItem('workflowConfig') !== null) {
      return JSON.parse(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        LZString.decompress(localStorage.getItem('workflowConfig')!),
      ) as WorkflowConfig;
    } else {
      return defaultWorkflowConfig;
    }
  }
}
